import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";

// Elegant single-viewport sticky reveal.
// Height = STEPS × 55vh - enough scroll for each morph to feel intentional,
// short enough to not drag.

const STEPS = [
  { pre: "You have", accent: "7 days.", tag: "01 / time" },
  { pre: "The org has", accent: "real problems.", tag: "02 / brief" },
  { pre: "Now,", accent: "what will you ship?", tag: "03 / you" },
];

export default function Manifesto() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end end"],
  });

  const stepProg = useTransform(scrollYProgress, [0, 1], [0, STEPS.length]);
  const bgGreen = useTransform(scrollYProgress, [0, 1], [0.04, 0.14]);
  const bgBlue = useTransform(scrollYProgress, [0, 1], [0.14, 0.04]);

  return (
    <section
      ref={ref}
      className="relative"
      style={{ height: `${STEPS.length * 55}vh` }}
      data-testid="manifesto-section"
    >
      <div className="sticky top-0 h-screen overflow-hidden">
        <div className="absolute inset-0 bg-[#050505]" />
        <motion.div
          className="absolute inset-0 pointer-events-none"
          style={{
            background: useTransform(
              bgGreen,
              (v) => `radial-gradient(ellipse 55% 45% at 25% 40%, rgba(47,204,124,${v}), transparent 65%)`
            ),
          }}
        />
        <motion.div
          className="absolute inset-0 pointer-events-none"
          style={{
            background: useTransform(
              bgBlue,
              (v) => `radial-gradient(ellipse 55% 45% at 75% 60%, rgba(45,123,240,${v}), transparent 65%)`
            ),
          }}
        />
        <div className="absolute inset-0 grid-bg opacity-25 [mask-image:radial-gradient(ellipse_at_center,black_15%,transparent_75%)]" />

        <div className="relative z-10 h-full w-full max-w-6xl mx-auto px-6 md:px-12 lg:px-24 py-16 md:py-20 flex flex-col justify-between">
          {/* Top: Section eyebrow */}
          <div className="flex items-center gap-3">
            <span className="w-8 h-px bg-[#2FCC7C]" />
            <span className="font-mono text-xs uppercase tracking-[0.3em] text-[#2FCC7C]">
              The premise
            </span>
          </div>

          {/* Middle: Morphing headline - vertically centered in remaining space */}
          <div className="relative flex-1 flex items-center py-8">
            <div className="relative w-full min-h-[180px] md:min-h-[220px] lg:min-h-[260px]">
              {STEPS.map((step, i) => (
                <Step key={i} index={i} progress={stepProg} step={step} />
              ))}
            </div>
          </div>

          {/* Bottom: Progress row */}
          <div className="flex items-center gap-4">
            <StepIndicator progress={stepProg} total={STEPS.length} />
            <div className="flex-1 flex items-center gap-2">
              {STEPS.map((_, i) => (
                <ProgressPip key={i} index={i} progress={stepProg} />
              ))}
            </div>
            <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-600 hidden sm:inline">
              scroll
            </span>
          </div>
        </div>
      </div>
    </section>
  );
}

function Step({ index, progress, step }) {
  const opacity = useTransform(
    progress,
    [index - 0.5, index, index + 0.6, index + 1],
    [0, 1, 1, 0]
  );
  const y = useTransform(
    progress,
    [index - 0.5, index, index + 0.6, index + 1],
    [24, 0, 0, -24]
  );

  return (
    <motion.div
      style={{ opacity, y }}
      className="absolute inset-0 flex flex-col justify-center"
    >
      <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-4">
        {step.tag}
      </div>
      <h2 className="font-heading text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-black tracking-tighter leading-[1.1]">
        <span className="text-zinc-500">{step.pre} </span>
        <span className="text-gradient-neon glow-text-green">{step.accent}</span>
      </h2>
    </motion.div>
  );
}

function StepIndicator({ progress, total }) {
  const current = useTransform(progress, (v) =>
    String(Math.min(total, Math.max(1, Math.ceil(v || 0.001)))).padStart(2, "0")
  );
  return (
    <div className="font-mono text-xs text-zinc-400 flex items-baseline gap-1 shrink-0">
      <motion.span className="text-[#2FCC7C] text-base tabular-nums">{current}</motion.span>
      <span className="text-zinc-600">/ {String(total).padStart(2, "0")}</span>
    </div>
  );
}

function ProgressPip({ index, progress }) {
  const width = useTransform(progress, [index, index + 1], ["0%", "100%"]);
  return (
    <div className="relative flex-1 h-[2px] bg-white/10 rounded-full overflow-hidden">
      <motion.div
        style={{ width }}
        className="absolute inset-y-0 left-0 bg-gradient-to-r from-[#2FCC7C] to-[#2D7BF0]"
      />
    </div>
  );
}
